import { useCallback, useEffect, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import { ActionType, EventAction, EventType } from '../types'
import { useTracking } from './useTracking'

interface UseTrackComponentParams {
  componentKey: string
  eventName: string
  source?: string
  description?: string
  props?: Readonly<Record<string, any>>
  state?: Readonly<Record<string, any>>
}

interface AdditionalData {
  eventName?: string
  description?: string
  uiKey?: string
  source?: string // Indicates where the event occurred — can be a page or a section (since same component could be reused)
  [key: string]: any
}

export const useTrackComponent = ({
  componentKey,
  eventName: defaultEventName,
  description: defaultDescription,
  source,
  props = {},
  state = {},
}: UseTrackComponentParams) => {
  const { dispatchEvent, resolveUiKey } = useTracking()
  const uiKey = resolveUiKey(componentKey)
  const previousValues = useRef<Readonly<Record<string, any>>>({ ...props, ...state })

  /**
   * Dispatch a tracking event.
   */
  const trackEvent = useCallback(
    (type: EventType, action: EventAction, additionalData: AdditionalData = {}) => {
      const { name = defaultEventName, description = defaultDescription, ...rest } = additionalData
      dispatchEvent({
        key: uiKey,
        type,
        name,
        action,
        description,
        additionalData: {
          source,
          ...rest,
        },
      })
    },
    [dispatchEvent, uiKey, defaultEventName, defaultDescription, source]
  )

  /**
   * Helper for tracking user interactions.
   */
  const trackUserInteraction = useCallback(
    (action: EventAction, eventData: AdditionalData = {}) => {
      trackEvent(EventType.USER_INTERACTION, action, eventData)
    },
    [trackEvent]
  )

  /**
   * Helper for tracking system events.
   */
  const trackSystemEvent = useCallback(
    (action: EventAction, eventData: AdditionalData = {}) => {
      trackEvent(EventType.SYSTEM_EVENT, action, eventData)
    },
    [trackEvent]
  )

  /**
   * Helper for tracking error events.
   */
  const trackErrorEvent = useCallback(
    (action: EventAction, eventData: AdditionalData = {}) => {
      trackEvent(EventType.ERROR_EVENT, action, eventData)
    },
    [trackEvent]
  )

  /**
   * Track changes in props or state.
   */
  useEffect(() => {
    const combinedValues = { ...props, ...state }

    Object.keys(combinedValues).forEach((key) => {
      if (combinedValues[key] !== previousValues.current[key]) {
        trackSystemEvent(
          { type: ActionType.UPDATE },
          {
            property: key,
            description: defaultDescription,
            eventName: defaultEventName,
            oldValue: previousValues.current[key],
            newValue: combinedValues[key],
          }
        )
      }
    })

    previousValues.current = combinedValues
  }, [props, state, trackSystemEvent, defaultEventName, defaultDescription])

  /**
   * Track component visibility.
   */
  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0,
    onChange: (inView) => {
      if (inView) {
        trackUserInteraction({ type: ActionType.VIEW })
      }
    },
  })

  return {
    trackEvent,
    trackUserInteraction,
    trackSystemEvent,
    trackErrorEvent,
    ref,
  }
}
